import React, { useState, useMemo, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X, CalendarIcon, MapPin, Eraser } from 'lucide-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectAllBrands,
  selectAllFlavors,
  selectAllAttributes,
} from '@/store/slices/globalDataSlice';
import { selectAllLocations } from '@/store/slices/locationsSlice';
import {
  setSearchTerm as setDiarySearchTerm,
  setIsPublicFilter as setDiaryIsPublicFilter,
  setMinRating as setDiaryMinRating,
  setMaxRating as setDiaryMaxRating,
  toggleBrandId as toggleDiaryBrandId,
  toggleFlavorId as toggleDiaryFlavorId,
  toggleAttributeId as toggleDiaryAttributeId,
  setSelectedLocationIds as setDiarySelectedLocationIds,
  toggleLocationType as toggleDiaryLocationType,
  setDateRange as setDiaryDateRange,
  resetDiaryFilters,
  selectDiarySearchTerm,
  selectDiaryIsPublicFilter,
  selectDiaryMinRating,
  selectDiaryMaxRating,
  selectDiarySelectedBrandIds,
  selectDiarySelectedFlavorIds,
  selectDiarySelectedAttributeIds,
  selectDiarySelectedLocationIds,
  selectDiarySelectedLocationTypes,
  selectDiaryDateRange,
} from '@/store/slices/diaryFiltersSlice';
import type { LocationType, FirestoreLocation } from '@/types/firestore';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from "@/components/ui/calendar";
import { Badge } from '@/components/ui/badge';
import { GroupedMultiSelect } from '@/components/ui/grouped-multi-select';
import { MultiSelect } from '@/components/ui/multi-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LocationSelect from '@/components/locations/LocationSelect';

export interface DiaryFilters {
  searchTerm: string;
  isPublic: 'all' | 'public' | 'private';
  minRating: number | null;
  maxRating: number | null;
  selectedFlavors: string[];
  selectedAttributes: string[];
  selectedBrands: string[];
  selectedLocations: string[];
  selectedLocationTypes: LocationType[];
  dateRange: { start: string | null; end: string | null };
  viewMode?: 'card' | 'table';
}

interface DiaryFilterControlsProps {
  // No props needed as it directly interacts with Redux store
}

// Define location type options available for filtering
const DIARY_LOCATION_TYPES: { value: LocationType; label: string }[] = [
  { value: 'shop', label: 'Shops' },
  { value: 'brewery', label: 'Breweries' },
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'other', label: 'Other' },
];

// Create filter badge interface
interface FilterBadge {
  id: string;
  type: 'brand' | 'flavor' | 'attribute' | 'location' | 'locationType' | 'rating' | 'date' | 'visibility';
  label: string;
  value: string | number | boolean | null;
}

const DiaryFilterControls: React.FC<DiaryFilterControlsProps> = () => {
  const dispatch = useAppDispatch();

  const searchTerm = useAppSelector(selectDiarySearchTerm);
  const isPublicFilter = useAppSelector(selectDiaryIsPublicFilter);
  const minRating = useAppSelector(selectDiaryMinRating);
  const maxRating = useAppSelector(selectDiaryMaxRating);
  const selectedBrandIds = useAppSelector(selectDiarySelectedBrandIds);
  const selectedFlavorIds = useAppSelector(selectDiarySelectedFlavorIds);
  const selectedAttributeIds = useAppSelector(selectDiarySelectedAttributeIds);
  const selectedLocationIds = useAppSelector(selectDiarySelectedLocationIds);
  const selectedLocationTypes = useAppSelector(selectDiarySelectedLocationTypes);
  const dateRange = useAppSelector(selectDiaryDateRange);

  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [activeFilterBadges, setActiveFilterBadges] = useState<FilterBadge[]>([]);
  const [isLocationSelectOpen, setIsLocationSelectOpen] = useState(false);

  const [localMinRating, setLocalMinRating] = useState<string>(minRating?.toString() || '');
  const [localMaxRating, setLocalMaxRating] = useState<string>(maxRating?.toString() || '');

  useEffect(() => setLocalMinRating(minRating?.toString() || ''), [minRating]);
  useEffect(() => setLocalMaxRating(maxRating?.toString() || ''), [maxRating]);

  const handleMinRatingChange = (value: string) => {
    setLocalMinRating(value);
    const num = parseFloat(value);
    dispatch(setDiaryMinRating(isNaN(num) ? null : num));
  };
  const handleMaxRatingChange = (value: string) => {
    setLocalMaxRating(value);
    const num = parseFloat(value);
    dispatch(setDiaryMaxRating(isNaN(num) ? null : num));
  };

  const allBrands = useAppSelector(selectAllBrands);
  const allFlavors = useAppSelector(selectAllFlavors);
  const allAttributes = useAppSelector(selectAllAttributes);
  const allLocations = useAppSelector(selectAllLocations) as FirestoreLocation[];

  const handleClearSearch = () => dispatch(setDiarySearchTerm(''));
  const handleResetAllFilters = () => dispatch(resetDiaryFilters());

  const locationTypeItems = useMemo(() => 
    DIARY_LOCATION_TYPES.map(lt => ({ id: lt.value, name: lt.label })), []
  );


  const handleDateSelect = (range: { from?: Date; to?: Date } | undefined) => {
    const startDate = range?.from ? range.from.toISOString() : null;
    const endDate = range?.to ? range.to.toISOString() : null;
    dispatch(setDiaryDateRange({ start: startDate, end: endDate }));
  };

  const handleConfirmLocationSelection = (newSelectedLocationIds: string[] | string | null) => {
    if (Array.isArray(newSelectedLocationIds)) {
        dispatch(setDiarySelectedLocationIds(newSelectedLocationIds));
    }
    setIsLocationSelectOpen(false); // Close the dialog after confirmation
  };

  const displayDateRange = useMemo(() => {
    const start = dateRange.start ? parseISO(dateRange.start) : null;
    const end = dateRange.end ? parseISO(dateRange.end) : null;
    const dateFormat = 'MMM d, yy';
    if (start && end) {
      if (start.getFullYear() === end.getFullYear()) {
        return `${format(start, 'MMM d')} - ${format(end, dateFormat)}`;
      }
      return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
    }
    if (start) return `From ${format(start, dateFormat)}`;
    if (end) return `To ${format(end, dateFormat)}`;
    return "Select date range...";
  }, [dateRange]);

  useEffect(() => {
    const badgesByType: Record<FilterBadge['type'], FilterBadge[]> = {
      'visibility': [], 'brand': [], 'flavor': [], 'attribute': [],
      'location': [], 'locationType': [], 'rating': [], 'date': []
    };
    if (isPublicFilter !== 'all') {
      badgesByType.visibility.push({id: `visibility-${isPublicFilter}`, type: 'visibility', label: isPublicFilter === 'public' ? 'Public Entries' : 'Private Entries', value: isPublicFilter});
    }
    selectedBrandIds.forEach(id => { const item = allBrands.find(i => i.id === id); if (item) badgesByType.brand.push({id: `brand-${id}`, type: 'brand', label: item.name, value: id}); });
    selectedFlavorIds.forEach(id => { const item = allFlavors.find(i => i.id === id); if (item) badgesByType.flavor.push({id: `flavor-${id}`, type: 'flavor', label: item.name, value: id}); });
    selectedAttributeIds.forEach(id => { const item = allAttributes.find(i => i.id === id); if (item) badgesByType.attribute.push({id: `attr-${id}`, type: 'attribute', label: item.name, value: id}); });
    selectedLocationIds.forEach(id => { const item = allLocations.find(i => i.id === id); if (item) badgesByType.location.push({id: `location-${id}`, type: 'location', label: item.name, value: id}); });
    selectedLocationTypes.forEach(val => { const item = DIARY_LOCATION_TYPES.find(t => t.value === val); if (item) badgesByType.locationType.push({id: `locType-${val}`, type: 'locationType', label: item.label, value: val}); });
    const ratingLabel = maxRating !== null || minRating !== null ? ((minRating !== null ? `Min ≥ ${minRating.toFixed(1)}` : '') + (minRating !== null && maxRating !== null ? ' ' : '') + (maxRating !== null ? ` Max ≤ ${maxRating.toFixed(1)}` : '')).trim() : null;
    if (ratingLabel) badgesByType.rating.push({id: 'rating', type: 'rating', label: ratingLabel, value: `${minRating ?? ''}-${maxRating ?? ''}`});
    const startDate = dateRange.start ? parseISO(dateRange.start) : null; const endDate = dateRange.end ? parseISO(dateRange.end) : null;
    if (startDate || endDate) { const dateLabel = (startDate ? `From ${format(startDate, 'PP')}` : '') + (startDate && endDate ? ' ' : '') + (endDate ? `To ${format(endDate, 'PP')}` : ''); badgesByType.date.push({id: 'date', type: 'date', label: dateLabel.trim(), value: `${dateRange.start || ''}|${dateRange.end || ''}`});}
    Object.keys(badgesByType).forEach(type => { if (type !== 'rating' && type !== 'date' && type !== 'visibility') badgesByType[type as FilterBadge['type']].sort((a,b) => a.label.localeCompare(b.label)); });
    setActiveFilterBadges([ ...badgesByType.visibility, ...badgesByType.rating, ...badgesByType.date, ...badgesByType.brand, ...badgesByType.flavor, ...badgesByType.attribute, ...badgesByType.location, ...badgesByType.locationType ]);
  }, [isPublicFilter, selectedBrandIds, selectedFlavorIds, selectedAttributeIds, selectedLocationIds, selectedLocationTypes, minRating, maxRating, dateRange, allBrands, allFlavors, allAttributes, allLocations]);

  const handleRemoveBadge = (badge: FilterBadge) => {
    switch (badge.type) {
      case 'visibility': dispatch(setDiaryIsPublicFilter('all')); break;
      case 'brand': dispatch(toggleDiaryBrandId(badge.value as string)); break;
      case 'flavor': dispatch(toggleDiaryFlavorId(badge.value as string)); break;
      case 'attribute': dispatch(toggleDiaryAttributeId(badge.value as string)); break;
      case 'location': 
        dispatch(setDiarySelectedLocationIds(selectedLocationIds.filter(id => id !== badge.value as string))); 
        break;
      case 'locationType': dispatch(toggleDiaryLocationType(badge.value as LocationType)); break;
      case 'rating': dispatch(setDiaryMinRating(null)); dispatch(setDiaryMaxRating(null)); break;
      case 'date': dispatch(setDiaryDateRange({ start: null, end: null })); break;
    }
  };

  const handleFlavorToggle = (id: string) => dispatch(toggleDiaryFlavorId(id));
  const handleAttributeToggle = (id: string) => dispatch(toggleDiaryAttributeId(id));
  const handleBrandToggle = (id: string) => dispatch(toggleDiaryBrandId(id));
  const handleLocationTypeToggle = (id: string) => dispatch(toggleDiaryLocationType(id as LocationType));

  return (
    <div className="max-h-full flex flex-col bg-card text-card-foreground rounded-lg border shadow-sm overflow-hidden">
      <div className="p-4 flex flex-row items-center justify-between border-b">
        <h3 className="text-lg font-semibold">Filter Options</h3>
      </div>
      <div className="flex-grow overflow-y-auto space-y-3 p-4">
          {/* Freetext Search */}
          <div>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="searchTerm"
                type="text"
                placeholder="Search reviews, drinks..."
                value={searchTerm}
                onChange={(e) => dispatch(setDiarySearchTerm(e.target.value))}
                className="w-full pl-9"
              />
              {searchTerm && (
                <Button 
                  variant="ghost" size="icon" 
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={handleClearSearch}
                  aria-label="Clear search"
                >
                  <X size={16} />
                </Button>
              )}
            </div>
          </div>

          {/* Visibility and Date Range Filters on the same row */}
          <div className="flex gap-2">
            <div className="flex-1">
              <div className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-0 py-0 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Select value={isPublicFilter} onValueChange={(value: 'all' | 'public' | 'private') => dispatch(setDiaryIsPublicFilter(value))}>
                  <SelectTrigger id="isPublicFilter" aria-label="Visibility" className="w-full h-full px-3 py-2 border-0 bg-transparent focus:ring-0 focus:ring-offset-0">
                    <SelectValue placeholder="Visibility" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Entries</SelectItem>
                    <SelectItem value="public">Public Entries</SelectItem>
                    <SelectItem value="private">Private Entries</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex-1">
              <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                  <PopoverTrigger asChild>
                      <div id="dateRangeFilter" className={`flex h-10 w-full cursor-pointer items-center justify-start rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${!(dateRange.start || dateRange.end) && "text-muted-foreground"}`}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        <span>{displayDateRange === "Select date range..." ? "Date Range" : displayDateRange}</span>
                      </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                          initialFocus
                          mode="range"
                          defaultMonth={dateRange.start ? parseISO(dateRange.start) : new Date()}
                          selected={{ from: dateRange.start ? parseISO(dateRange.start) : undefined, to: dateRange.end ? parseISO(dateRange.end) : undefined }}
                          onSelect={(range) => handleDateSelect(range)}
                          numberOfMonths={1}
                      />
                  </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Rating Filter */}
          <div>
            <div className="flex gap-2">
              <Input type="number" placeholder="Min Rating" value={localMinRating} onChange={(e) => handleMinRatingChange(e.target.value)} min="0" max="5" step="0.1" className="w-full"/>
              <Input type="number" placeholder="Max Rating" value={localMaxRating} onChange={(e) => handleMaxRatingChange(e.target.value)} min="0" max="5" step="0.1" className="w-full"/>
            </div>
          </div>

          {/* Brand Filter - Using the new MultiSelect component */}
          <div>
            <MultiSelect
              items={allBrands || []}
              selectedIds={selectedBrandIds}
              onToggleItem={handleBrandToggle}
              placeholder="Select Brands"
              emptyMessage="No brands found"
              searchPlaceholder="Search brands..."
              disabled={(allBrands || []).length === 0}
            />
          </div>
          
          {/* Flavor Filter - Using the GroupedMultiSelect component */}
          <div>
            <GroupedMultiSelect
              items={allFlavors}
              selectedIds={selectedFlavorIds}
              onToggleItem={handleFlavorToggle}
              placeholder="Select Flavors"
              emptyMessage="No flavors found"
              searchPlaceholder="Search flavors..."
              disabled={(allFlavors || []).length === 0}
              hideGroupTitles={true}
            />
          </div>

          {/* Attribute Filter - Using the GroupedMultiSelect component */}
          <div>
            <GroupedMultiSelect
              items={allAttributes}
              selectedIds={selectedAttributeIds}
              onToggleItem={handleAttributeToggle}
              placeholder="Select Attributes"
              emptyMessage="No attributes found"
              searchPlaceholder="Search attributes..."
              disabled={(allAttributes || []).length === 0}
              hideGroupTitles={true}
            />
          </div>

          {/* Location Type Filter - Using the new MultiSelect component */}
          <div>
            <MultiSelect
              items={locationTypeItems}
              selectedIds={selectedLocationTypes}
              onToggleItem={handleLocationTypeToggle}
              placeholder="Select Location Types"
              emptyMessage="No location types found"
              searchPlaceholder="Search location types..."
            />
          </div>

          {/* Specific Location Filter */}
          <div>
            <div 
                id="locationFilterTrigger"
                onClick={() => setIsLocationSelectOpen(true)}
                className="flex h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
            >
                <span className={selectedLocationIds.length === 0 ? 'text-muted-foreground' : 'text-card-foreground'}>
                    {selectedLocationIds.length > 0 ? `${selectedLocationIds.length} location${selectedLocationIds.length > 1 ? 's' : ''} selected` : "Select Locations"}
                </span>
                <MapPin className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </div>
          </div>

          {/* Filter Badges */}
          {activeFilterBadges.length > 0 && (
            <div className="flex flex-col gap-2 pt-3">
              {/* All badges in a single group without titles */}
              <div className="flex flex-wrap gap-1">
                {activeFilterBadges.map((badge) => (
                  <Badge key={badge.id} variant="secondary" className="group relative px-2 py-1 text-xs">
                    {badge.label}
                    <button onClick={() => handleRemoveBadge(badge)} className="ml-1 -mr-1 p-0.5 rounded-full opacity-50 group-hover:opacity-100 hover:bg-muted transition-opacity" aria-label={`Remove ${badge.label} filter`}><X size={10} /></button>
                  </Badge>
                ))}
              </div>
              
              {/* Clear All button */}
              {activeFilterBadges.length > 1 && (<Button variant="ghost" size="sm" onClick={handleResetAllFilters} className="text-muted-foreground text-xs w-fit px-2 py-1 h-auto mt-1" aria-label="Reset all filters"><Eraser size={10} className="mr-1" /> Clear All</Button>)}
            </div>
          )}
      </div>
      {isLocationSelectOpen && (
        <Dialog open={isLocationSelectOpen} onOpenChange={setIsLocationSelectOpen}>
          <DialogContent className="sm:max-w-[80vw] max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Select Locations</DialogTitle>
            </DialogHeader>
            <div className="flex-grow overflow-hidden">
              <LocationSelect 
                allLocations={allLocations}
                selectedLocationIds={selectedLocationIds}
                onSelectionChange={() => {}}
                mode="multi"
                onConfirm={handleConfirmLocationSelection}
                onCancel={() => setIsLocationSelectOpen(false)}
                listMapAreaHeight="h-[500px]"
                containerClassName="h-full"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default DiaryFilterControls; 